import { useState } from 'react';
import { MessageCircle, Loader2, ThumbsUp, MapPin } from 'lucide-react';

import { useFeed, useToggleReaction, type FeedPost } from '../../hooks/student/useFeed';
import PostMediaGrid from './PostMediaGrid';
import PostComments from './PostComments';

// ─── Reaction Emoji Map ─────────────────────────────────────────────────────

const REACTION_EMOJI: Record<string, string> = {
  like: '\uD83D\uDC4D',
  love: '\u2764\uFE0F',
  laugh: '\uD83D\uDE02',
  wow: '\uD83D\uDE2E',
  sad: '\uD83D\uDE22',
  angry: '\uD83D\uDE21',
  fire: '\uD83D\uDD25',
  clap: '\uD83D\uDC4F',
};

const QUICK_REACTIONS = ['like', 'love', 'fire', 'laugh', 'clap', 'wow'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function getInitials(first: string | null, last: string | null): string {
  return `${(first || '?')[0]}${(last || '')[0] || ''}`.toUpperCase();
}

// ─── Reaction Picker ────────────────────────────────────────────────────────

function ReactionPicker({ onPick }: { onPick: (type: string) => void }) {
  return (
    <div className="flex gap-1 bg-[#2A2626] border border-[#504A4A] rounded-full px-2 py-1.5 shadow-xl">
      {QUICK_REACTIONS.map((type) => (
        <button
          key={type}
          onClick={() => onPick(type)}
          className="text-lg hover:scale-125 transition-transform px-1"
          title={type}
        >
          {REACTION_EMOJI[type]}
        </button>
      ))}
    </div>
  );
}

// ─── Post Card ──────────────────────────────────────────────────────────────

function PostCard({ post }: { post: FeedPost }) {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const toggleReaction = useToggleReaction();

  const totalReactions = post.reactions?.reduce((sum, r) => sum + r.count, 0) ?? 0;
  const topReactions = (post.reactions || [])
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  const handleReaction = (type: string) => {
    setShowReactionPicker(false);
    toggleReaction.mutate({ postId: post.id, reactionType: type });
  };

  return (
    <div className="bg-[#221F1F] border border-[#504A4A] rounded-xl overflow-hidden hover:border-[#6B6565] transition-colors">
      {/* Author header */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        {post.avatar_url ? (
          <img src={post.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#9B1421]/30 flex items-center justify-center text-sm font-semibold text-[#EBFFEE]">
            {getInitials(post.first_name, post.last_name)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#EBFFEE] truncate">
            {post.first_name} {post.last_name}
          </p>
          <div className="flex items-center gap-2 text-[11px] text-[#A9A3A3]">
            <span>{timeAgo(post.created_at)}</span>
            {post.location_name && (
              <>
                <span className="text-[#504A4A]">&middot;</span>
                <span className="flex items-center gap-0.5">
                  <MapPin className="w-2.5 h-2.5" />
                  {post.location_name}
                </span>
              </>
            )}
            {post.is_pinned && (
              <>
                <span className="text-[#504A4A]">&middot;</span>
                <span className="text-[#E8535E] font-medium">Pinned</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-2">
        <p className="text-[15px] text-[#EBFFEE]/95 whitespace-pre-wrap leading-relaxed">
          {post.content}
        </p>
      </div>

      {/* Media Grid */}
      {post.media_urls && post.media_urls.length > 0 && (
        <div className="px-4">
          <PostMediaGrid media={post.media_urls} />
        </div>
      )}

      {/* Reaction summary + comment count */}
      {(totalReactions > 0 || post.comments_count > 0) && (
        <div className="px-4 py-2 flex items-center justify-between text-xs text-[#A9A3A3]">
          <div className="flex items-center gap-1.5">
            {topReactions.length > 0 && (
              <span className="flex -space-x-0.5">
                {topReactions.map((r) => (
                  <span key={r.type} className="text-sm">{REACTION_EMOJI[r.type] || r.type}</span>
                ))}
              </span>
            )}
            {totalReactions > 0 && <span>{totalReactions}</span>}
          </div>
          {post.comments_count > 0 && (
            <span>{post.comments_count} {post.comments_count === 1 ? 'comment' : 'comments'}</span>
          )}
        </div>
      )}

      {/* Action bar */}
      <div className="border-t border-[#504A4A]/50 px-2 py-1 flex items-center">
        <div className="relative flex-1">
          <button
            onClick={() => setShowReactionPicker(!showReactionPicker)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-white/5 transition-colors w-full justify-center"
          >
            <ThumbsUp className="w-4 h-4" />
            Like
          </button>
          {showReactionPicker && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-10">
              <ReactionPicker onPick={handleReaction} />
            </div>
          )}
        </div>
        <button
          onClick={() => {
            // Scroll to or toggle comments — PostComments handles its own state
            const el = document.getElementById(`comments-${post.id}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-white/5 transition-colors flex-1 justify-center"
        >
          <MessageCircle className="w-4 h-4" />
          Comment
        </button>
      </div>

      {/* Comments section */}
      <div id={`comments-${post.id}`} className="px-4 pb-3">
        <PostComments postId={post.id} commentsCount={post.comments_count} />
      </div>
    </div>
  );
}

// ─── Feed Widget ─────────────────────────────────────────────────────────────

export default function FeedWidget() {
  const { posts, isLoading } = useFeed();

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#A9A3A3]" />
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-xl p-10 text-center">
        <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-3">
          <MessageCircle className="w-7 h-7 text-[#504A4A]" />
        </div>
        <h3 className="text-sm font-semibold text-[#EBFFEE]">No posts yet</h3>
        <p className="text-xs text-[#A9A3A3] mt-1.5 max-w-[240px] mx-auto">
          Posts from your gym community will appear here. Check back soon!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}
    </div>
  );
}
